import type { Contest } from '@betterpool/api-types';

// eslint-disable-next-line no-restricted-syntax
export enum NotificationType {
  // users
  USER_SIGN_UP = 'user-sign-up',
  USER_KYC_STATUS_UPDATED = 'user-kyc-status-updated',
  // contests
  NEW_ENTRY = 'new-entry',
  CONTEST_INVITE = 'contest-invite',
  CONTEST_JOINED = 'contest-joined',
  CONTEST_CANCELLED = 'contest-cancelled',
  CONTEST_WON = 'contest-won',
  CONTEST_EDITED = 'contest-edited',
  QUICK_PICKS_CONTEST_WON = 'quick-picks-contest-won',
  CONTEST_POST_MESSAGE_PUBLISHED = 'contest-post-message-published',
  // transactions
  DEPOSIT_CONFIRMED = 'deposit-confirmed',
  WITHDRAWAL_CONFIRMED = 'withdrawal-confirmed',
  BONUS_CONFIRMED = 'bonus-confirmed',
  CHANNEL_FOLLOWED = 'channel-followed',
  // bonuses
  DEPOSIT_BONUS = 'deposit-bonus-attached',
  // marketplace
  MARKETPLACE_OFFER_RECEIVED = 'marketplace-offer-received',
  MARKETPLACE_OFFER_COUNTERED = 'marketplace-offer-countered',
  MARKETPLACE_OFFER_ACCEPTED = 'marketplace-offer-accepted',
  MARKETPLACE_OFFER_ACCEPTED_WITH_PICK_CONTROL = 'marketplace-offer-accepted-with-pick-control',
  MARKETPLACE_GROUP_SALE_CONSENT_GIVEN = 'marketplace-group-sale-consent-given',
  MARKETPLACE_GROUP_SALE_ALL_CONSENTED = 'marketplace-group-sale-all-consented',
  MARKETPLACE_GROUP_SALE_CONSENT_WITHDRAWN = 'marketplace-group-sale-consent-withdrawn',
  MARKETPLACE_GROUP_SALE_LISTED = 'marketplace-group-sale-listed',
  MARKETPLACE_GROUP_SALE_LISTING_ENDED = 'marketplace-group-sale-listing-ended',
  MARKETPLACE_GROUP_SALE_SOLD = 'marketplace-group-sale-sold',
}

type NotificationContest = Pick<Contest, 'id' | 'name' | 'tags' | 'league'> & {
  thumbnailUrl: Contest['thumbnail_url'];
  contestType: Contest['contest_type'];
};

type User = {
  id: string;
};

type ContestCreatedPayload = {
  user: User;
  contest: NotificationContest;
};

type Sender = {
  userId: string;
};

type Recipient = {
  userId: string;
};

type BaseNotification = {
  id: string;
  type: NotificationType;
  senderChannel?: Channel;
  readAt?: string;
  createdAt: string;
  fallbackMessage: string;
};

type ContestInvitePayload = {
  user: User;
  contest: NotificationContest;
  sender: Sender;
  recipient: Recipient;
};

export type ContestUpdatedPayload = {
  contestId: string;
  contestName: string;
  updates: {
    title: string;
    current: string | number;
    previous: string | number;
  }[];
  sender: Sender;
};

export type QuickPickContestWonPayload = {
  slipId: string;
  userId: string;
  payoutAmount: number;
};

export type CommissionerPostPayload = {
  contest?: {
    id: string;
    name: string;
  };
  post?: {
    author?: {
      handle: string;
      avatarUrl?: string;
    };
    header: string;
    message: string;
  };
};

export type DepositBonusPayload = {
  user: {
    id: string;
  };
  bonus: {
    id: string;
    name: string;
    amount?: number;
    percentage?: number;
    calculationType?: 'PERCENTAGE' | 'FLAT';
  };
};

export type MarketplaceOfferPayload = {
  contestId: string;
  entryId: string;
  offerId?: string;
  listingId?: string | null;
  entryNumber?: number;
  contestName?: string;
  priceCents?: number;
  shareBps?: number;
  buyerFeeCents?: number;
  assignsPickControl?: boolean;
  expiresAt?: string;
  buyerHandle?: string;
  sellerHandle?: string;
  pickControllerHandle?: string;
};

export type MarketplaceGroupSalePayload = {
  contestId: string;
  entryId: string;
  listingId?: string | null;
  contestName?: string;
  entryName?: string;
};

export type Channel = {
  id: string;
  userId: string;
  name: string;
  handle: string;
  avatarUrl: string;
};

// Individual notification type interfaces for type safety
export type ChannelFollowedNotification = BaseNotification & {
  type: NotificationType.CHANNEL_FOLLOWED;
  payload: ContestCreatedPayload;
};

export type ContestInviteNotification = BaseNotification & {
  type: NotificationType.CONTEST_INVITE;
  payload: ContestInvitePayload;
};

export type ContestEditedNotification = BaseNotification & {
  type: NotificationType.CONTEST_EDITED;
  payload: ContestUpdatedPayload;
};

export type QuickPicksContestWonNotification = BaseNotification & {
  type: NotificationType.QUICK_PICKS_CONTEST_WON;
  payload: QuickPickContestWonPayload;
};

export type CommissionerPostNotification = BaseNotification & {
  type: NotificationType.CONTEST_POST_MESSAGE_PUBLISHED;
  payload?: CommissionerPostPayload;
};

export type DepositBonusNotification = BaseNotification & {
  type: NotificationType.DEPOSIT_BONUS;
  payload: DepositBonusPayload;
};

export type MarketplaceOfferReceivedNotification = BaseNotification & {
  type: NotificationType.MARKETPLACE_OFFER_RECEIVED;
  payload: MarketplaceOfferPayload;
};

export type MarketplaceOfferCounteredNotification = BaseNotification & {
  type: NotificationType.MARKETPLACE_OFFER_COUNTERED;
  payload: MarketplaceOfferPayload;
};

export type MarketplaceOfferAcceptedNotification = BaseNotification & {
  type:
    | NotificationType.MARKETPLACE_OFFER_ACCEPTED
    | NotificationType.MARKETPLACE_OFFER_ACCEPTED_WITH_PICK_CONTROL;
  payload: MarketplaceOfferPayload;
};

export type MarketplaceGroupSaleNotification = BaseNotification & {
  type:
    | NotificationType.MARKETPLACE_GROUP_SALE_CONSENT_GIVEN
    | NotificationType.MARKETPLACE_GROUP_SALE_ALL_CONSENTED
    | NotificationType.MARKETPLACE_GROUP_SALE_CONSENT_WITHDRAWN
    | NotificationType.MARKETPLACE_GROUP_SALE_LISTED
    | NotificationType.MARKETPLACE_GROUP_SALE_LISTING_ENDED
    | NotificationType.MARKETPLACE_GROUP_SALE_SOLD;
  payload: MarketplaceGroupSalePayload;
};

// Union type for polymorphic functions that work with any notification type
export type Notification =
  | ChannelFollowedNotification
  | ContestInviteNotification
  | ContestEditedNotification
  | QuickPicksContestWonNotification
  | CommissionerPostNotification
  | DepositBonusNotification
  | MarketplaceOfferReceivedNotification
  | MarketplaceOfferCounteredNotification
  | MarketplaceOfferAcceptedNotification
  | MarketplaceGroupSaleNotification;

export type GetNotificationsResponse = {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  id: string;
  data: Notification[];
};

export type GetNotificationsCountResponse = {
  id: string;
  data: {
    count: number;
  };
};

export type ReadNotificationsResponse = {
  id: string;
  data: {
    notificationIds: string[];
  };
};

export type BaseNotificationGroup = {
  id: string;
  type: NotificationType;
  readAt: string | null;
  createdAt: string;
  updateAt: string;
};

export type NotificationGroup = BaseNotificationGroup & {
  type: NotificationType.CONTEST_EDITED;
  payload: ContestUpdatedPayload;
};

export type GetLatestUnreadNotificationGroup = NotificationGroup;
